import { getStore, RootState } from '../../../init-store';
import { bind } from './decorators';
import { EventEmitter } from './event-emitter';
import {
    CONNECTION_TIME_MEASURE,
    createDrawingStatsCounter,
    DRAWING_TIME_MEASURE,
    DrawingStats,
    DrawingStatsCounter,
    FRAME_TIME_MEASURE,
} from './performance';

const DEFAULT_COLLECT_STATS_INTERVAL = 10000;
const FPS_BUFFER_SIZE = 60;

const PIXEL_THRESHOLD_HD = 500_000; // ~SD/HD boundary (~720p = 921K)
const PIXEL_THRESHOLD_FULL_HD = 2_000_000; // ~HD/FullHD boundary (~1080p = 2.07M)
const PIXEL_THRESHOLD_QHD = 4_000_000; // ~FullHD/QHD boundary (~1440p = 3.69M)
const PIXEL_THRESHOLD_4K = 8_000_000; // ~QHD/4K boundary (~2160p = 8.29M)

export interface DrawingPerformanceStatsReport extends DrawingStats {
    numberOfParticipantsWithVideo: number;
    rtcConnectionTime?: number;
    videosSd?: number;
    videosHd?: number;
    videosFullHd?: number;
    videosQhd?: number;
    videos4k?: number;
    totalVideoPixels?: number;
}

export class DrawingPerformanceStats extends EventEmitter {
    private _collectStatsTimer: ReturnType<typeof setInterval> | null = null;
    private _performanceObserver?: PerformanceObserver = undefined;
    private _drawingStatsCounter: DrawingStatsCounter;
    private _connectionTime: number | null = null;
    private _collectStatsInterval: number;
    constructor({ interval = DEFAULT_COLLECT_STATS_INTERVAL }: { interval?: number } = {}) {
        super();
        this._drawingStatsCounter = createDrawingStatsCounter(FPS_BUFFER_SIZE);
        this._collectStatsInterval = interval;
    }

    public startJob() {
        if (!this._collectStatsTimer) {
            this._collectStatsTimer = setInterval(this._collectStats, this._collectStatsInterval);
            this._runPerformanceObserver();
        }
    }

    public stopJob() {
        if (this._collectStatsTimer) {
            clearInterval(this._collectStatsTimer);
            this._collectStatsTimer = null;
            this._performanceObserver?.disconnect();
            delete this._performanceObserver;
        }
    }

    public destroy() {
        this.stopJob();
        this.removeAllListeners();
    }

    private _runPerformanceObserver() {
        const { countDrawingTime, countFrameRate } = this._drawingStatsCounter;

        this._performanceObserver = new PerformanceObserver((entries) => {
            entries.getEntriesByName(CONNECTION_TIME_MEASURE).forEach((connectionTime) => {
                this._connectionTime = connectionTime.duration;
            });
            entries.getEntriesByName(DRAWING_TIME_MEASURE).forEach((drawTime) => {
                countDrawingTime(drawTime.duration);
            });
            entries.getEntriesByName(FRAME_TIME_MEASURE).forEach((frameTime) => {
                countFrameRate(frameTime.duration);
            });
            // чистим измерения из кэша после обработки
            performance.clearMeasures(CONNECTION_TIME_MEASURE);
            performance.clearMeasures(DRAWING_TIME_MEASURE);
            performance.clearMeasures(FRAME_TIME_MEASURE);
        });
        this._performanceObserver.observe({ type: 'measure', buffered: true });
    }

    private _countParticipantsWithVideo(state: RootState) {
        const { participants, participantsByIds } = state.call.conference;
        return participants.reduce((acc, participantId) => {
            const participant = participantsByIds[participantId];
            if (participant?.video && !participant.video?.muted) {
                return acc + 1;
            }
            return acc;
        }, 0);
    }

    private _collectVideoResolutionStats() {
        const buckets = {
            videosSd: 0,
            videosHd: 0,
            videosFullHd: 0,
            videosQhd: 0,
            videos4k: 0,
            totalVideoPixels: 0,
        };

        document.querySelectorAll<HTMLVideoElement>('video').forEach((video) => {
            const pixels = video.videoWidth * video.videoHeight;

            if (pixels <= 0) {
                return;
            }

            buckets.totalVideoPixels += pixels;

            if (pixels < PIXEL_THRESHOLD_HD) {
                buckets.videosSd++;
            } else if (pixels < PIXEL_THRESHOLD_FULL_HD) {
                buckets.videosHd++;
            } else if (pixels < PIXEL_THRESHOLD_QHD) {
                buckets.videosFullHd++;
            } else if (pixels < PIXEL_THRESHOLD_4K) {
                buckets.videosQhd++;
            } else {
                buckets.videos4k++;
            }
        });

        return buckets;
    }

    @bind
    private async _collectStats() {
        const state = getStore().getState();
        const drawingStats = this._drawingStatsCounter?.getCurrentDrawingStats();

        const numberOfParticipantsWithVideo = this._countParticipantsWithVideo(state);
        const videoResolutionStats = this._collectVideoResolutionStats();

        const statsReport: DrawingPerformanceStatsReport = {
            numberOfParticipantsWithVideo,
            ...drawingStats,
            ...videoResolutionStats,
        };

        if (this._connectionTime) {
            statsReport.rtcConnectionTime = this._connectionTime;
            // чистим время подключения, чтобы не повторяться
            this._connectionTime = null;
        }

        this.emit('performance-stats', statsReport);
    }
}
